import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ChevronUp,
  Edit3,
  History,
  Info,
  Loader2,
  Lock,
  MessageCircle,
  RotateCcw,
  ScrollText,
  Share2,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import { AiWatermark } from "@/components/ai-watermark";
import { MARKDOWN_REMARK_PLUGINS } from "@/lib/markdown";
import { toast } from "sonner";

import { BaziBoard } from "@/components/bazi/bazi-board";
import { MysticChat } from "@/components/mystic-chat";
import { SiteHeader } from "@/components/site-header";
import { TrustProbeDialog } from "@/components/trust-probe-dialog";
import { ShareDialog } from "@/components/share/share-dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  BAZI_READING_PRICE,
  castBaziReading,
  createTrustProbeCharts,
  createSession,
  deleteBaziChart,
  getBaziChart,
  getBaziReading,
  getChatHistory,
  getSession,
  CHARTS_PAGE_SIZE,
  listBaziCharts,
  lunarBirthDay,
  type BaziChart,
  type BaziChartSummary,
  type BaziReadingGod,
  type BaziReading,
  type ChatMessage,
} from "@/lib/boide-api";
import { displayName, refreshBoideUser, useBoideUser } from "@/hooks/use-boide-user";
import { errorName, trackEvent } from "@/lib/analytics";
import { useRegisterTour } from "@/components/tour/tour-provider";
import {
  chartPrefillSearch,
  parseChartPrefill,
  type ChartPrefillSearch,
} from "@/lib/chart-prefill";
import { UnknownHourCta } from "@/components/doan-gio/unknown-hour-cta";

export const Route = createFileRoute("/bat-tu")({
  validateSearch: (
    search: Record<string, unknown>,
  ): { session?: string; chart?: string } & ChartPrefillSearch => ({
    // `?ho_ten=&ngay=&gio=&gioi_tinh=` — điền sẵn form rồi tự lập lá số (từ trang Đoán giờ sinh).
    ...chartPrefillSearch(search),
    session: typeof search.session === "string" ? search.session : undefined,
    // `?chart=` mở thẳng MỘT lá số đã lập — đích của trang Lịch sử (tab Luận giải).
    // Khác `?session=`: phần lớn lá số chưa từng có phiên đàm đạo nào, nên vào bằng
    // `session` là không có đường nào mở lại chúng.
    chart: typeof search.chart === "string" ? search.chart : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Lập Lá Số Bát Tự, Tứ Trụ Miễn Phí - Luận Giải Ngũ Hành" },
      {
        name: "description",
        content:
          "Lập lá số Bát Tự, Tứ Trụ miễn phí: thiên can địa chi, nhật chủ, thập thần, ngũ hành và đại vận. Lập lá số ngay!",
      },
      { property: "og:title", content: "Lập Lá Số Bát Tự, Tứ Trụ Miễn Phí - Luận Giải Ngũ Hành" },
      { property: "og:description", content: "Xem tứ trụ, nhật chủ, ngũ hành và đại vận." },
      { property: "og:url", content: "https://boide.online/bat-tu" },
    ],
    links: [{ rel: "canonical", href: "https://boide.online/bat-tu" }],
  }),
  component: BatTuPage,
});

const READING_MD_COMPONENTS = {
  strong: ({ children }: { children?: ReactNode }) => (
    <strong className="font-semibold text-[#E5B869]">{children}</strong>
  ),
  h2: ({ children }: { children?: ReactNode }) => (
    <h2 className="font-serif-soft italic text-[#E5B869] text-xl mt-6 mb-3 border-b border-[#E5B869]/10 pb-1">
      {children}
    </h2>
  ),
  h3: ({ children }: { children?: ReactNode }) => (
    <h3 className="font-serif-soft italic text-[#E5B869] text-lg mt-5 mb-2">{children}</h3>
  ),
  p: ({ children }: { children?: ReactNode }) => (
    <p className="mb-4 last:mb-0 leading-[1.8] text-foreground/82 dark:!text-[#EAE0D5]">
      {children}
    </p>
  ),
  li: ({ children }: { children?: ReactNode }) => (
    <li className="flex gap-2 items-start leading-[1.8] text-foreground/82 dark:!text-[#EAE0D5]">
      <span className="text-[#E5B869] text-[12px] mt-[6px] opacity-80 select-none">✦</span>
      <div className="flex-1">{children}</div>
    </li>
  ),
  ul: ({ children }: { children?: ReactNode }) => (
    <ul className="flex flex-col gap-2 my-3 list-none !pl-0 !ml-0">{children}</ul>
  ),
};

function parseGodSection(
  text: string,
  keyword: string,
): { elements: string[]; desc: string } | null {
  const cleanText = text.replace(/\*\*/g, "");
  const lines = cleanText.split("\n");
  for (const line of lines) {
    const lowerLine = line.toLowerCase();
    const index = lowerLine.indexOf(keyword.toLowerCase());
    if (index !== -1) {
      let after = line.substring(index + keyword.length).trim();
      after = after.replace(/^(?:[:\-=\s]|là|của|bao gồm)+/, "").trim();

      const elementRegex = /^(Hỏa|Mộc|Thổ|Kim|Thủy)(?:\s*,\s*(Hỏa|Mộc|Thổ|Kim|Thủy))*/i;
      const match = after.match(elementRegex);
      if (match) {
        const elementPart = match[0];
        let descPart = after.substring(elementPart.length).trim();
        descPart = descPart
          .replace(/^(?:\s|[(),;:-]|\[)+/, "")
          .replace(/(?:\s|[(),;:]|\])+$/, "")
          .trim();
        return {
          elements: elementPart
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean),
          desc: descPart,
        };
      }
    }
  }
  return null;
}

function stripMarkdown(text: string): string {
  return text
    .replace(/\*\*/g, "")
    .replace(/`/g, "")
    .replace(/^#+\s*/gm, "")
    .replace(/^\s*[-*]\s*/gm, "")
    .replace(/\n+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function normalizeGod(
  god: BaziReadingGod | null | undefined,
): { elements: string[]; desc: string } | null {
  if (!god || !Array.isArray(god.elements) || typeof god.content_md !== "string") return null;
  const elements = god.elements.map((item) => item.trim()).filter(Boolean);
  const desc = stripMarkdown(god.content_md);
  if (!elements.length && !desc) return null;
  return { elements, desc };
}

const ELEMENT_TEXT_COLORS: Record<string, string> = {
  Mộc: "text-emerald-700 dark:text-[#88F7B5] dark:drop-shadow-[0_0_8px_rgba(136,247,181,0.5)]",
  Hỏa: "text-rose-700 dark:text-[#FF8A8A] dark:drop-shadow-[0_0_8px_rgba(255,138,138,0.5)]",
  Thổ: "text-amber-700 dark:text-[#FFE380] dark:drop-shadow-[0_0_8px_rgba(255,227,128,0.5)]",
  Kim: "text-zinc-700 dark:text-[#FFFFFF] dark:drop-shadow-[0_0_8px_rgba(255,255,255,0.4)]",
  Thủy: "text-sky-700 dark:text-[#8ad4ff] dark:drop-shadow-[0_0_8px_rgba(138,212,255,0.5)]",
};

function truncateDesc(desc: string, maxLen = 45): string {
  if (!desc) return "";
  const clean = desc
    .replace(/^\s*\(\s*/, "")
    .replace(/\s*\)\s*$/, "")
    .trim();
  if (clean.length <= maxLen) return clean;
  return clean.slice(0, maxLen) + "...";
}

function parseDate(value: string) {
  const [year, month, day] = value.split("-").map((x) => parseInt(x, 10));
  if (!year || !month || !day) return null;
  return { year, month, day };
}

const POETIC_SUBTEXTS: Record<string, string> = {
  tinh_cach: "Gương lòng soi tỏ bản tâm.",
  "tính cách": "Gương lòng soi tỏ bản tâm.",
  "tính cách & hành vi": "Gương lòng soi tỏ bản tâm.",
  su_nghiep: "Cánh chim tung gió vượt trùng khơi.",
  "sự nghiệp": "Cánh chim tung gió vượt trùng khơi.",
  "sự nghiệp & quan lộc": "Cánh chim tung gió vượt trùng khơi.",
  tai_loc: "Bạc vàng tụ tán theo cơ duyên.",
  "tài lộc": "Bạc vàng tụ tán theo cơ duyên.",
  "tài lộc & gia sản": "Bạc vàng tụ tán theo cơ duyên.",
  tinh_duyen: "Sợi chỉ đỏ buộc vào ai?",
  "tình duyên": "Sợi chỉ đỏ buộc vào ai?",
  "tình duyên & hôn nhân": "Sợi chỉ đỏ buộc vào ai?",
  gia_dao: "Bến đỗ bình yên qua giông bão.",
  "gia đạo": "Bến đỗ bình yên qua giông bão.",
  "gia đạo & lục thân": "Bến đỗ bình yên qua giông bão.",
  suc_khoe: "Giữ chân nguyên giữa vạn biến.",
  "sức khỏe": "Giữ chân nguyên giữa vạn biến.",
  "sức khỏe & tật ách": "Giữ chân nguyên giữa vạn biến.",
};

function getPoeticSubtext(key: string, label?: string): string {
  const cleanKey = key.toLowerCase().trim();
  const cleanLabel = (label || "").toLowerCase().trim();
  return (
    POETIC_SUBTEXTS[cleanKey] || POETIC_SUBTEXTS[cleanLabel] || "Khơi mở thiên cơ, tỏ tường vận số."
  );
}

const MagicalDivider = () => (
  <div className="flex items-center justify-center w-full my-6 select-none opacity-80">
    <div className="h-[1px] flex-1 bg-gradient-to-r from-transparent via-[#E5B869]/80 to-[#E5B869]" />
    <div className="mx-4 text-[#E5B869] animate-[spin_20s_linear_infinite]">
      <svg
        className="w-6 h-6"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M12 2a5 5 0 0 0 0 10 5 5 0 0 1 0 10" />
        <circle cx="12" cy="7" r="1.5" fill="currentColor" />
        <circle cx="12" cy="17" r="1.5" fill="currentColor" />
      </svg>
    </div>
    <div className="h-[1px] flex-1 bg-gradient-to-l from-transparent via-[#E5B869]/80 to-[#E5B869]" />
  </div>
);

function BatTuPage() {
  const { user } = useBoideUser();
  const search = Route.useSearch();
  const { session, chart: chartParam } = search;
  // Chốt MỘT lần lúc mount (URL bị dọn ngay sau khi áp) — xem `/tu-vi`.
  const [prefill] = useState(() =>
    search.session || search.chart ? null : parseChartPrefill(search),
  );
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { confirm, confirmDialog } = useConfirm();
  // Danh sách lá số PHÂN TRANG (10/trang). Drawer "Lá số Bát Tự đã lập" có nút sang
  // trang; danh sách rút gọn trong form dùng chính trang đang chọn.
  const [chartsPage, setChartsPage] = useState(1);
  const savedCharts = useQuery({
    queryKey: ["bazi-charts", { page: chartsPage, limit: CHARTS_PAGE_SIZE }],
    queryFn: () => listBaziCharts(chartsPage, CHARTS_PAGE_SIZE),
    enabled: Boolean(user),
    placeholderData: keepPreviousData,
  });
  const savedChartItems = savedCharts.data?.items ?? [];
  const savedChartsTotal = savedCharts.data?.total ?? 0;
  const savedChartsPages = Math.max(1, Math.ceil(savedChartsTotal / CHARTS_PAGE_SIZE));
  // Lá số đầu tiên khai mở miễn phí; từ lá số thứ 2 trừ phí. FE tính giá để hiển thị
  // trước, BE vẫn là nguồn quyết định cuối cùng.
  // 🔴 Đọc `has_any_reading` (BE đếm trên TOÀN BỘ lá số) chứ KHÔNG suy từ 10 dòng của
  // trang đang xem: người có 30 lá số đã khai mở sẽ được hứa "Miễn phí" rồi bị trừ tiền.
  const isFirstReadingFree = !savedCharts.data?.has_any_reading;
  const readingPrice = isFirstReadingFree ? 0 : BAZI_READING_PRICE;
  const readingPriceLabel =
    readingPrice === 0 ? "Miễn phí" : `${readingPrice.toLocaleString("vi-VN")}đ`;
  const [form, setForm] = useState<{
    name: string;
    date: string;
    time: string;
    gender: string;
    isLunar: boolean;
    /** Tháng âm NHUẬN — chỉ có nghĩa khi isLunar; thiếu là quy đổi lệch ~1 tháng. */
    isLeapMonth?: boolean;
  }>({
    name: "",
    date: "",
    time: "",
    gender: "nam",
    isLunar: false,
  });
  // Có prefill từ URL thì KHÔNG điền từ hồ sơ (hồ sơ sẽ đè giới tính của người được xem).
  const hasPrefilledProfileRef = useRef(Boolean(prefill));
  const [chart, setChart] = useState<BaziChart | null>(null);
  const [isFormCollapsed, setIsFormCollapsed] = useState(true);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<"chart" | "reading">("chart");

  const [reading, setReading] = useState<BaziReading | null>(null);
  const [readingLoading, setReadingLoading] = useState(false);
  const castingRef = useRef(false);
  const [showChat, setShowChat] = useState(false);
  const [activeSection, setActiveSection] = useState<{
    key: string;
    label?: string;
    content_md: string;
  } | null>(null);

  const [historySessionId, setHistorySessionId] = useState<string | undefined>(undefined);
  const [historyMessages, setHistoryMessages] = useState<ChatMessage[]>([]);
  // Có prefill → lượt này lập lá số MỚI, không tự mở lá số gần nhất.
  const hasAutoloadedRef = useRef(Boolean(prefill));
  const [trustProbeId, setTrustProbeId] = useState<string | null>(null);
  const [trustProbeOpen, setTrustProbeOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [chartsDrawerOpen, setChartsDrawerOpen] = useState(false);
  const [shareMaskActive, setShareMaskActive] = useState(false);
  const chartCaptureRef = useRef<HTMLDivElement | null>(null);

  // Giờ sinh BẮT BUỘC: nó dựng trụ giờ — thiếu thì lá số là phỏng đoán, mà trước
  // đây `parseInt(hourRaw || "0")` lặng lẽ lấp giờ Tý.
  const canSubmit = useMemo(
    () => Boolean(form.name.trim() && parseDate(form.date) && form.time),
    [form],
  );
  const justCreatedChartRef = useRef(false);

  useEffect(() => {
    if (hasPrefilledProfileRef.current || !user?.profile) return;
    hasPrefilledProfileRef.current = true;
    const profile = user.profile;
    setForm((current) => {
      const gender = String(profile.gender || "").toLowerCase();
      const profileGender =
        gender === "nữ" || gender === "nu" || gender === "female"
          ? "nu"
          : gender === "nam" || gender === "male"
            ? "nam"
            : current.gender;
      // `lunar_birth_date` của BE là CHUỖI hiển thị ("24/06/1997 ÂL"), không phải
      // số ngày — nhét thẳng vào chuỗi ngày sẽ ra "1997-06-24/06/1997 ÂL".
      const lunarDay = lunarBirthDay(profile);
      const hasLunarBirthDate =
        profile.birth_calendar === "lunar" &&
        Boolean(lunarDay && profile.lunar_birth_month && profile.lunar_birth_year);
      const profileDate = hasLunarBirthDate
        ? `${profile.lunar_birth_year}-${String(profile.lunar_birth_month).padStart(2, "0")}-${String(lunarDay).padStart(2, "0")}`
        : profile.birth_date || "";
      return {
        ...current,
        name: current.name || displayName(user, ""),
        date: current.date || profileDate,
        time: current.time || profile.birth_time?.slice(0, 5) || "",
        gender: profileGender,
        isLunar: current.date ? current.isLunar : hasLunarBirthDate,
        isLeapMonth: current.date
          ? current.isLeapMonth
          : hasLunarBirthDate && Boolean(profile.lunar_is_leap_month),
      };
    });
  }, [user]);

  // Scroll to top when form collapses or expands to prevent layout shifts/jumps
  useEffect(() => {
    if (typeof window === "undefined") return;
    const container = document.querySelector('[data-tour="battu-scroll-container"]');
    if (container) {
      container.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [isFormCollapsed]);

  const formatShortDate = (dateStr: string) => {
    if (!dateStr) return "";
    const clean = dateStr.replace(/\s+/g, "");
    const parts = clean.includes("-") ? clean.split("-") : clean.split("/");
    if (parts.length === 3) {
      if (parts[0].length === 4) {
        // YYYY-MM-DD
        const dd = parts[2];
        const mm = parts[1];
        const yy = parts[0].slice(-2);
        return `${dd}/${mm}/${yy}`;
      }
      // DD/MM/YYYY
      const dd = parts[0];
      const mm = parts[1];
      const yy = parts[2].slice(-2);
      return `${dd}/${mm}/${yy}`;
    }
    return dateStr;
  };

  // Register tour for Bát Tự based on current phase
  useRegisterTour(
    !isFormCollapsed ? "battu" : chart ? (reading ? "battu-reading" : "battu-board") : "battu",
    !loading && !trustProbeOpen && !readingLoading,
  );

  // Scroll and state coordination for Bát Tự tour
  useEffect(() => {
    if (typeof window === "undefined") return;

    const scrollToBattuStep = (tourId: string, stepIndex: number) => {
      if (tourId === "battu") {
        if (stepIndex === 1 || stepIndex === 2) {
          setIsFormCollapsed(false);
        }
      } else if (tourId === "battu-board") {
        if (stepIndex >= 1 && stepIndex <= 5) {
          if (chart) setIsFormCollapsed(true);
          setActiveTab("chart");
        }
      } else if (tourId === "battu-reading") {
        setActiveTab("reading");
      }

      let stepSelectors: string[] = [];
      if (tourId === "battu") {
        stepSelectors = ["body", '[data-tour="battu-form"]', '[data-tour="battu-submit"]'];
      } else if (tourId === "battu-board") {
        stepSelectors = [
          "body",
          '[data-tour="battu-board"]',
          '[data-tour="battu-nguhanh"]',
          '[data-tour="battu-diachi"]',
          '[data-tour="battu-daivan"]',
          '[data-tour="battu-khaimo-mobile"]',
        ];
      } else if (tourId === "battu-reading") {
        stepSelectors = [
          "body",
          '[data-tour="battu-gods"]',
          '[data-tour="battu-daicuc-anchor"]',
          '[data-tour="battu-sections"]',
          '[data-tour="battu-chat"]',
        ];
      }

      const selector = stepSelectors[stepIndex];
      if (!selector || selector === "body") return;

      const container = document.querySelector('[data-tour="battu-scroll-container"]');

      const isVisible = (el: HTMLElement) => {
        const rect = el.getBoundingClientRect();
        const style = window.getComputedStyle(el);
        return (
          rect.width > 0 &&
          rect.height > 0 &&
          style.display !== "none" &&
          style.visibility !== "hidden"
        );
      };

      const pickVisibleElement = (sel: string): HTMLElement | null => {
        const elements = Array.from(document.querySelectorAll(sel));
        for (const el of elements) {
          if (el instanceof HTMLElement && isVisible(el)) {
            return el;
          }
        }
        const first = document.querySelector(sel);
        return first instanceof HTMLElement ? first : null;
      };

      let target = pickVisibleElement(selector);
      if (tourId === "battu-board" && stepIndex === 5) {
        if (!target || !isVisible(target)) {
          target = pickVisibleElement('[data-tour="battu-khaimo"]');
        }
      }

      if (container instanceof HTMLElement && target instanceof HTMLElement) {
        const containerRect = container.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();
        const relativeTop = targetRect.top - containerRect.top + container.scrollTop;

        container.scrollTo({
          top: Math.max(0, relativeTop - 24),
          behavior: "auto",
        });

        // Dispatch resize event immediately so Joyride recalculates coordinates instantly
        window.dispatchEvent(new Event("resize"));

        // Keep a brief interval to handle any potential layout shifts or rendering delays
        const interval = setInterval(() => {
          window.dispatchEvent(new Event("resize"));
        }, 50);
        setTimeout(() => {
          clearInterval(interval);
          window.dispatchEvent(new Event("resize"));
        }, 300);
      }
    };

    const handleTourEvent = (e: Event) => {
      const { tourId, stepIndex, type } = (e as CustomEvent).detail || {};
      if (tourId !== "battu" && tourId !== "battu-board" && tourId !== "battu-reading") return;
      if (type === "step:after" || type === "tour:start" || type === "step:before") {
        scrollToBattuStep(tourId, stepIndex);
      }
    };

    const handlePrescroll = (e: Event) => {
      const { tourId, stepIndex } = (e as CustomEvent).detail || {};
      if (tourId !== "battu" && tourId !== "battu-board" && tourId !== "battu-reading") return;
      scrollToBattuStep(tourId, stepIndex);
    };

    window.addEventListener("tour-event", handleTourEvent);
    window.addEventListener("tour-prescroll", handlePrescroll);
    return () => {
      window.removeEventListener("tour-event", handleTourEvent);
      window.removeEventListener("tour-prescroll", handlePrescroll);
    };
  }, [chart, reading]);

  useEffect(() => {
    if (!session) return;
    let cancelled = false;
    (async () => {
      try {
        const [detail, history] = await Promise.all([getSession(session), getChatHistory(session)]);
        if (cancelled) return;
        const baziChartId = detail.metadata?.bazi_chart_id;
        if (baziChartId) {
          const [chartDetail, cachedReading] = await Promise.all([
            getBaziChart(baziChartId),
            getBaziReading(baziChartId).catch(() => null),
          ]);
          if (cancelled) return;
          setChart(chartDetail);
          setReading(cachedReading);
          setIsFormCollapsed(true);
          setForm({
            name: chartDetail.ho_ten || "Người xem",
            date: `${chartDetail.nam_sinh}-${String(chartDetail.thang_sinh).padStart(2, "0")}-${String(chartDetail.ngay_sinh).padStart(2, "0")}`,
            time: `${String(chartDetail.gio_sinh).padStart(2, "0")}:${String(chartDetail.phut_sinh).padStart(2, "0")}`,
            gender: chartDetail.gioi_tinh === "nu" || chartDetail.gioi_tinh === "nữ" ? "nu" : "nam",
            isLunar: false,
          });
        }
        setHistorySessionId(detail.id);
        setHistoryMessages(history.messages);
        setShowChat(true);
      } catch (err) {
        if (!cancelled) {
          toast.error(err instanceof Error ? err.message : "Không tải được phiên Bát Tự");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session]);

  // Nạp luận giải đã cache khi đổi lá số. CHỈ phụ thuộc chart.id — KHÔNG phụ thuộc
  // `user` để tránh việc refreshBoideUser() sau mỗi lượt chat làm reset/biến mất
  // khung luận giải + chat. Lỗi tạm thời cũng KHÔNG xoá reading đang hiển thị.
  const chartId = chart?.id ?? null;
  useEffect(() => {
    if (!chartId) {
      setReading(null);
      return;
    }
    let cancelled = false;
    getBaziReading(chartId)
      .then((r) => {
        if (!cancelled && r) setReading(r);
      })
      .catch(() => {
        /* giữ nguyên reading hiện tại nếu fetch lỗi */
      });
    return () => {
      cancelled = true;
    };
  }, [chartId]);

  const gods = useMemo(() => {
    if (!reading) return null;
    const structured = {
      dungThan: normalizeGod(reading.dung_than),
      hyThan: normalizeGod(reading.hy_than),
      kyThan: normalizeGod(reading.ky_than),
    };
    if (structured.dungThan || structured.hyThan || structured.kyThan) {
      return structured;
    }
    const allText = [reading.dai_cuc, ...reading.sections.map((s) => s.content_md)].join("\n");
    const dungThan = parseGodSection(allText, "Dụng thần");
    const hyThan = parseGodSection(allText, "Hỷ thần");
    const kyThan = parseGodSection(allText, "Kỵ thần");
    return { dungThan, hyThan, kyThan };
  }, [reading]);

  const { leftSections, rightSections } = useMemo(() => {
    if (!reading) return { leftSections: [], rightSections: [] };
    const half = Math.ceil(reading.sections.length / 2);
    const left = reading.sections.slice(0, half);
    const right = reading.sections.slice(half);
    return { leftSections: left, rightSections: right };
  }, [reading]);

  async function submit(input: typeof form = form) {
    const parsed = parseDate(input.date);
    if (!parsed) {
      toast.error("Ngày sinh chưa hợp lệ");
      return;
    }
    if (!input.time) {
      toast.error("Vui lòng chọn giờ sinh trước khi lập lá số");
      return;
    }
    const [hourRaw, minuteRaw] = input.time.split(":");
    setLoading(true);
    setReading(null);
    setShowChat(false);
    setHistorySessionId(undefined);
    setHistoryMessages([]);
    setActiveTab("chart");
    try {
      const nextPair = await createTrustProbeCharts({
        source: "bazi",
        use_profile: false,
        ho_ten: input.name.trim() || "Người xem",
        nam: parsed.year,
        thang: parsed.month,
        ngay: parsed.day,
        gio: parseInt(hourRaw, 10),
        phut: parseInt(minuteRaw || "0", 10),
        gioi_tinh: input.gender as "nam" | "nu",
        is_lunar: input.isLunar,
        is_leap_month: input.isLunar && Boolean(input.isLeapMonth),
      });
      const next = nextPair.bazi_chart;
      setChart(next);
      setTrustProbeId(nextPair.probe.id);
      // setTrustProbeOpen(true); // Tạm thời tắt tính năng hỏi lại khi lập lá số
      setIsFormCollapsed(true);
      justCreatedChartRef.current = true;

      // Lá số mới xuất hiện ngay trong danh sách "Lá số đã lập".
      void qc.invalidateQueries({ queryKey: ["bazi-charts"] });
      trackEvent("bazi_chart_created", { gender: input.gender, is_lunar: input.isLunar });
      trackEvent("trust_probe_created", {
        source: "bazi",
        status: nextPair.probe.status,
      });
    } catch (error) {
      trackEvent("bazi_chart_failed", { error: errorName(error) });
      toast.error(error instanceof Error ? error.message : "Không lập được lá số Bát Tự");
    } finally {
      setLoading(false);
    }
  }

  // Nạp lại một lá số đã lập trước đó (từ danh sách "Lá số đã lập"). Khác với khôi
  // phục từ Lịch sử: ở đây chỉ nạp lá số + luận giải đã cache, chưa gắn phiên chat.
  const [loadingChartId, setLoadingChartId] = useState<string | null>(null);
  async function loadChart(chartId: string) {
    if (loadingChartId || chartId === chart?.id) return;
    setLoadingChartId(chartId);
    setShowChat(false);
    setHistorySessionId(undefined);
    setHistoryMessages([]);
    setActiveTab("chart");
    try {
      const [chartDetail, cachedReading] = await Promise.all([
        getBaziChart(chartId),
        getBaziReading(chartId).catch(() => null),
      ]);
      setChart(chartDetail);
      setReading(cachedReading);
      setIsFormCollapsed(true);
      setForm({
        name: chartDetail.ho_ten || "Người xem",
        date: `${chartDetail.nam_sinh}-${String(chartDetail.thang_sinh).padStart(2, "0")}-${String(chartDetail.ngay_sinh).padStart(2, "0")}`,
        time: `${String(chartDetail.gio_sinh).padStart(2, "0")}:${String(chartDetail.phut_sinh).padStart(2, "0")}`,
        gender: chartDetail.gioi_tinh === "nu" || chartDetail.gioi_tinh === "nữ" ? "nu" : "nam",
        isLunar: false,
      });
      trackEvent("bazi_chart_reopened", {});
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Không tải được lá số đã lập");
    } finally {
      setLoadingChartId(null);
    }
  }

  // Điền sẵn từ URL rồi tự lập lá số. Dọn URL ngay để tải lại trang không lập thêm lá số.
  const prefillStartedRef = useRef(false);
  useEffect(() => {
    if (!prefill || prefillStartedRef.current) return;
    prefillStartedRef.current = true;
    const pad = (n: number) => String(n).padStart(2, "0");
    const next = {
      name: prefill.name,
      date: `${prefill.year}-${pad(prefill.month)}-${pad(prefill.day)}`,
      time: prefill.time,
      gender: prefill.gender,
      isLunar: false,
    };
    setForm(next);
    void navigate({ to: "/bat-tu", search: {}, replace: true });
    void submit(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill]);

  // Mở thẳng lá số được chỉ trong URL (`/bat-tu?chart=…`) — đích của trang Lịch sử.
  //
  // ⚠️ Chỉ chạy MỘT lần cho mỗi id: `loadChart` đặt `chart` state, và nếu effect chạy
  // lại sau khi người xem đã bấm sang lá số khác thì URL sẽ kéo họ ngược về lá số cũ.
  const openedChartParamRef = useRef<string | null>(null);
  useEffect(() => {
    if (!chartParam || session) return;
    if (openedChartParamRef.current === chartParam) return;
    openedChartParamRef.current = chartParam;
    void loadChart(chartParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartParam, session]);

  // Xoá một lá số đã lập. Nếu xoá lá số đang xem thì reset về form nhập mới.
  const [deletingChartId, setDeletingChartId] = useState<string | null>(null);
  async function deleteChart(chartId: string) {
    if (deletingChartId) return;
    const name = savedChartItems.find((c) => c.id === chartId)?.ho_ten || "Ẩn danh";
    const ok = await confirm({
      title: "Xoá lá số Bát Tự này?",
      description: (
        <>
          Lá số “{name}” và phần khai mở đã trả tiền sẽ bị xoá. Hành động này không thể hoàn tác và
          không hoàn tiền.
        </>
      ),
      confirmLabel: "Xoá lá số",
    });
    if (!ok) return;
    setDeletingChartId(chartId);
    try {
      await deleteBaziChart(chartId);
      if (chart?.id === chartId) {
        setChart(null);
        setReading(null);
        setShowChat(false);
        setHistorySessionId(undefined);
        setHistoryMessages([]);
        setActiveTab("chart");
        setIsFormCollapsed(false);
      }
      void qc.invalidateQueries({ queryKey: ["bazi-charts"] });
      trackEvent("bazi_chart_deleted", {});
      toast.success("Đã xoá lá số");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Không xoá được lá số");
    } finally {
      setDeletingChartId(null);
    }
  }

  useEffect(() => {
    if (session || chart || loadingChartId || savedChartItems.length === 0) {
      return;
    }
    // URL đã chỉ đích danh một lá số → KHÔNG tự nạp lá số mới nhất. Hai lượt nạp chạy
    // song song thì lượt nào về sau thắng, và người bấm từ Lịch sử có thể rơi vào một
    // lá số hoàn toàn khác lá số họ chọn.
    if (chartParam) return;
    if (hasAutoloadedRef.current) return;
    hasAutoloadedRef.current = true;

    const latestChart = savedChartItems[0];
    void loadChart(latestChart.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, chart, chartParam, loadingChartId, savedChartItems]);

  // `refreshStale`: lá số đã được BE hiệu chỉnh sau khi khai mở → khai mở lại MIỄN PHÍ
  // (BE tự đặt giá 0 khi bài luận mang cờ stale), nên không hỏi xác nhận phí.
  async function openReading(refreshStale = false) {
    if (!chart) return;
    if (
      !refreshStale &&
      readingPrice > 0 &&
      !window.confirm(
        `Khai mở luận giải toàn bộ lá số sẽ tính phí ${readingPrice.toLocaleString("vi-VN")}đ. Tiếp tục?`,
      )
    ) {
      return;
    }
    // State `readingLoading` chưa kịp cập nhật giữa hai cú bấm liền nhau — ref chặn ngay.
    if (castingRef.current) return;
    castingRef.current = true;
    setReadingLoading(true);
    try {
      const r = await castBaziReading(chart.id, refreshStale);
      setReading(r);
      setActiveTab("reading");
      trackEvent("bazi_reading_opened", { cached: Boolean(r.cached) });

      // Tạo phiên Bát Tự ngay khi khai mở luận giải để lá số xuất hiện trong Lịch sử,
      // kể cả khi người dùng chưa chat. MysticChat sẽ tái dùng phiên này khi hỏi thêm.
      if (!historySessionId) {
        try {
          const s = await createSession({
            subject: `Lá số Bát Tự của ${chart.ho_ten}`,
            fortune_type: "bazi",
            bazi_chart_id: chart.id,
          });
          setHistorySessionId(s.session_id);
          void qc.invalidateQueries({ queryKey: ["boide-sessions"] });
        } catch (sessionError) {
          // Không chặn việc xem luận giải nếu tạo phiên lỗi.
          trackEvent("bazi_session_create_failed", { error: errorName(sessionError) });
        }
      }
      // Cập nhật số dư trên header (khai mở lá số thứ 2+ bị trừ phí).
      void refreshBoideUser();
    } catch (error) {
      trackEvent("bazi_reading_failed", { error: errorName(error) });
      toast.error(error instanceof Error ? error.message : "Không khai mở được lá số");
    } finally {
      castingRef.current = false;
      setReadingLoading(false);
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col text-foreground dark:text-white">
      <main
        data-tour="battu-scroll-container"
        className="app-feature-bg app-feature-bg--bat-tu desktop-chart-background desktop-chart-background--bat-tu min-h-0 flex-1 overflow-y-auto animate-fade-in"
      >
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-5 px-3 pb-24 pt-5 sm:px-4 md:px-6">
          {chart && isFormCollapsed ? (
            <div className="flex items-center gap-2 w-full">
              <button
                type="button"
                onClick={() => setIsFormCollapsed(false)}
                className="group flex flex-1 items-center gap-2 sm:gap-3 rounded-full border border-gold-soft bg-[#FAF6F0] dark:bg-card/45 px-4 sm:px-5 py-2.5 text-left backdrop-blur transition hover:border-primary/60 overflow-hidden shadow-sm"
              >
                <Sparkles className="h-3.5 w-3.5 text-primary shrink-0 animate-pulse" />
                <span className="font-serif-soft text-base italic text-gold-gradient truncate flex items-center gap-1.5">
                  <span>{chart.ho_ten}</span>
                  <span className="text-[11px] sm:text-[13px] text-muted-foreground font-sans not-italic truncate">
                    ·{" "}
                    {formatShortDate(
                      chart.ngay_sinh + "/" + chart.thang_sinh + "/" + chart.nam_sinh,
                    )}{" "}
                    (DL)
                  </span>
                </span>
                <span className="ml-auto inline-flex items-center gap-1.5 text-[11px] text-[#8A8077] transition group-hover:text-primary shrink-0">
                  <Edit3 className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Sửa</span>
                </span>
              </button>

              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={deletingChartId === chart.id}
                className="hidden md:flex rounded-full border-destructive/40 bg-transparent text-destructive hover:bg-destructive/10 h-10 w-10 p-0 transition-all duration-300 items-center justify-center disabled:opacity-50 shrink-0"
                onClick={() => deleteChart(chart.id)}
                aria-label="Xoá lá số"
                title="Xoá lá số"
              >
                {deletingChartId === chart.id ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Trash2 className="h-3.5 w-3.5" />
                )}
              </Button>
            </div>
          ) : null}

          {/* Mobile Tab Switcher */}
          {chart && isFormCollapsed && (
            <div className="md:hidden rounded-2xl border border-gold-soft/40 bg-[#FAF6F0] dark:bg-card/40 p-1 mb-2 max-w-sm mx-auto w-full shadow-sm">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setActiveTab("chart")}
                  className={`flex-1 flex h-10 items-center justify-center gap-2 rounded-xl text-sm font-semibold font-serif-soft transition duration-300 ${
                    activeTab === "chart"
                      ? "bg-white dark:bg-zinc-800 text-[#3E2723] dark:text-[#E5B869] shadow-sm border border-gold-soft/20"
                      : "text-[#8A8077] dark:text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Sparkles className="h-4 w-4 text-primary" />
                  Lá số
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab("reading")}
                  className={`flex-1 flex h-10 items-center justify-center gap-2 rounded-xl text-sm font-semibold font-serif-soft transition duration-300 ${
                    activeTab === "reading"
                      ? "bg-white dark:bg-zinc-800 text-[#3E2723] dark:text-[#E5B869] shadow-sm border border-gold-soft/20"
                      : "text-[#8A8077] dark:text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <ScrollText className="h-4 w-4 text-primary" />
                  Luận giải
                </button>
              </div>

              <div
                className="mt-1 grid grid-cols-[1.2fr_1fr_1fr_2.5rem] gap-1 border-t border-gold-soft/30 pt-1"
                aria-label="Tác vụ lá số"
              >
                <button
                  type="button"
                  onClick={() => setChartsDrawerOpen(true)}
                  className="flex h-10 min-w-0 items-center justify-center gap-1.5 rounded-xl text-[11px] font-medium text-foreground transition hover:bg-primary/10 active:bg-primary/15"
                >
                  <History className="h-4 w-4 shrink-0 text-primary" />
                  <span className="truncate">Lá số đã lập</span>
                </button>
                <button
                  type="button"
                  disabled={!reading}
                  onClick={() => setShowChat(true)}
                  className="flex h-10 min-w-0 items-center justify-center gap-1.5 rounded-xl text-[11px] font-medium text-foreground transition hover:bg-primary/10 active:bg-primary/15 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <MessageCircle className="h-4 w-4 shrink-0 text-primary" />
                  <span className="truncate">Đàm đạo</span>
                </button>
                <button
                  type="button"
                  onClick={() => setShareOpen(true)}
                  className="flex h-10 min-w-0 items-center justify-center gap-1.5 rounded-xl text-[11px] font-medium text-foreground transition hover:bg-primary/10 active:bg-primary/15"
                >
                  <Share2 className="h-4 w-4 shrink-0 text-primary" />
                  <span className="truncate">Chia sẻ</span>
                </button>
                <button
                  type="button"
                  disabled={deletingChartId === chart.id}
                  onClick={() => void deleteChart(chart.id)}
                  className="flex h-10 w-10 items-center justify-center rounded-xl text-destructive transition hover:bg-destructive/10 active:bg-destructive/15 disabled:opacity-50"
                  aria-label="Xoá lá số"
                  title="Xoá lá số"
                >
                  {deletingChartId === chart.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>
          )}

          <section
            className={
              chart && isFormCollapsed
                ? "w-full"
                : "grid gap-4 lg:grid-cols-[360px_1fr] w-full min-w-0"
            }
          >
            {(!chart || !isFormCollapsed) && (
              <form
                data-tour="battu-form"
                onSubmit={(event) => {
                  event.preventDefault();
                  if (canSubmit && !loading) void submit();
                }}
                className="border border-gold-soft bg-card/65 p-4 lg:sticky lg:top-4 lg:self-start rounded-2xl shadow-mystic backdrop-blur min-w-0"
              >
                <div className="flex items-center gap-2 text-[#E5B869]">
                  <Sparkles className="h-4 w-4" />
                  <h1 className="font-serif-soft text-xl">Lá số Bát Tự</h1>
                </div>
                <div className="mt-4 flex flex-col gap-4">
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="bazi-name">Họ tên</Label>
                    <Input
                      id="bazi-name"
                      value={form.name}
                      onChange={(event) =>
                        setForm((prev) => ({ ...prev, name: event.target.value }))
                      }
                      className="border-border/60 bg-background/60 dark:border-white/15 dark:bg-black/30"
                    />
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="flex flex-col gap-2">
                      <Label htmlFor="bazi-date">Ngày sinh</Label>
                      <Input
                        id="bazi-date"
                        type="date"
                        value={form.date}
                        onChange={(event) =>
                          setForm((prev) => ({ ...prev, date: event.target.value }))
                        }
                        className="border-border/60 bg-background/60 dark:border-white/15 dark:bg-black/30"
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <Label htmlFor="bazi-time">
                        Giờ sinh<span className="text-red-400"> *</span>
                      </Label>
                      <Input
                        id="bazi-time"
                        type="time"
                        required
                        aria-invalid={!form.time}
                        value={form.time}
                        onChange={(event) =>
                          setForm((prev) => ({ ...prev, time: event.target.value }))
                        }
                        className="border-border/60 bg-background/60 dark:border-white/15 dark:bg-black/30"
                      />
                    </div>
                  </div>
                  <UnknownHourCta />
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="bazi-gender">Giới tính</Label>
                    <Select
                      value={form.gender}
                      onValueChange={(value) => setForm((prev) => ({ ...prev, gender: value }))}
                    >
                      <SelectTrigger
                        id="bazi-gender"
                        className="border-border/60 bg-background/60 dark:border-white/15 dark:bg-black/30"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="nam">Nam</SelectItem>
                        <SelectItem value="nu">Nữ</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-foreground/72 dark:text-white/72">
                    <Checkbox
                      checked={form.isLunar}
                      onCheckedChange={(checked) =>
                        setForm((prev) => ({
                          ...prev,
                          isLunar: checked === true,
                          isLeapMonth: checked === true ? prev.isLeapMonth : false,
                        }))
                      }
                    />
                    Ngày nhập là âm lịch
                  </label>
                  {form.isLunar ? (
                    <label className="flex items-center gap-2 text-sm text-foreground/72 dark:text-white/72">
                      <Checkbox
                        checked={Boolean(form.isLeapMonth)}
                        onCheckedChange={(checked) =>
                          setForm((prev) => ({ ...prev, isLeapMonth: checked === true }))
                        }
                      />
                      Tháng nhuận
                    </label>
                  ) : null}
                  <div className="flex flex-col gap-2">
                    <div className="flex gap-2">
                      <Button
                        data-tour="battu-submit"
                        type="submit"
                        disabled={!canSubmit || loading}
                        className="flex-1 bg-[#E5B869] text-black hover:bg-[#f0c979]"
                      >
                        {loading ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <Sparkles className="mr-2 h-4 w-4" />
                        )}
                        Lập lá số
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        className="border-border/60 bg-background/30 dark:border-white/15 dark:bg-transparent"
                        onClick={() => {
                          setForm({
                            name: "",
                            date: "",
                            time: "",
                            gender: "nam",
                            isLunar: false,
                          });
                          setIsFormCollapsed(false);
                        }}
                        aria-label="Xóa dữ liệu đã nhập"
                        title="Xóa dữ liệu đã nhập"
                      >
                        <RotateCcw className="h-4 w-4" />
                      </Button>
                    </div>
                    {chart && (
                      <Button
                        type="button"
                        variant="ghost"
                        className="w-full text-muted-foreground hover:text-foreground text-xs h-8 border border-border/50 bg-background/40 hover:bg-accent/50 dark:text-white/40 dark:hover:text-white/85 dark:border-white/5 dark:bg-white/5 dark:hover:bg-white/10"
                        onClick={() => setIsFormCollapsed(true)}
                      >
                        Thu gọn lại
                      </Button>
                    )}
                  </div>
                  <p className="text-[12px] leading-relaxed text-muted-foreground dark:text-white/40">
                    Chưa rõ về Bát Tự?{" "}
                    <Link
                      to="/bat-tu-chi-tiet"
                      className="text-[#E5B869] underline-offset-2 hover:underline"
                    >
                      Đọc bài giới thiệu Tứ Trụ
                    </Link>
                    .
                  </p>

                  {!chart && user && savedChartItems.length > 0 ? (
                    <div className="border-t border-border/35 pt-4 dark:border-white/5">
                      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground dark:text-white/50">
                        <History className="h-3.5 w-3.5 text-[#E5B869]/70" />
                        Lá số đã lập
                      </div>
                      <div className="mt-2 flex max-h-52 flex-col gap-1.5 overflow-y-auto pr-1">
                        {savedChartItems.map((c: BaziChartSummary) => {
                          const isActive = chart ? c.id === (chart as BaziChart).id : false;
                          const isLoadingThis = loadingChartId === c.id;
                          return (
                            <button
                              key={c.id}
                              type="button"
                              disabled={Boolean(loadingChartId)}
                              onClick={() => loadChart(c.id)}
                              className={`flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-left text-xs transition disabled:opacity-60 ${
                                isActive
                                  ? "border-[#E5B869]/50 bg-[#E5B869]/10"
                                  : "border-border/50 bg-background/45 hover:border-primary/30 hover:bg-primary/5 dark:border-white/8 dark:bg-black/20 dark:hover:border-[#E5B869]/30 dark:hover:bg-[#E5B869]/5"
                              }`}
                            >
                              <span className="min-w-0 flex-1">
                                <span className="block truncate font-medium text-foreground/85 dark:text-white/85">
                                  {c.ho_ten || "Người xem"}
                                </span>
                                <span className="text-[10px] text-muted-foreground dark:text-white/40">
                                  {c.gioi_tinh === "nu" || c.gioi_tinh === "nữ" ? "Nữ" : "Nam"} ·{" "}
                                  {c.nam_sinh}
                                  {c.day_master ? ` · Nhật chủ ${c.day_master}` : ""}
                                </span>
                              </span>
                              <span className="flex shrink-0 items-center gap-2">
                                {isLoadingThis ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin text-[#E5B869]" />
                                ) : isActive ? (
                                  <span className="text-[10px] text-[#E5B869]">Đang xem</span>
                                ) : null}
                                <span
                                  role="button"
                                  tabIndex={-1}
                                  aria-label="Xoá lá số"
                                  title="Xoá lá số"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteChart(c.id);
                                  }}
                                  className="text-destructive/70 transition hover:text-destructive"
                                >
                                  {deletingChartId === c.id ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                  ) : (
                                    <Trash2 className="h-3.5 w-3.5" />
                                  )}
                                </span>
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}
                </div>
              </form>
            )}

            <div
              className={`min-h-[420px] min-w-0 ${activeTab === "chart" ? "block" : "hidden md:block"}`}
            >
              {chart ? (
                <>
                  <div className="mb-3 hidden justify-end md:flex">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="rounded-full border-gold-soft/60 bg-card/50 text-primary hover:border-primary/60"
                      onClick={() => setShareOpen(true)}
                    >
                      <Share2 className="mr-2 h-4 w-4" />
                      Chia sẻ
                    </Button>
                  </div>
                  <div ref={chartCaptureRef}>
                    <BaziBoard chart={chart} maskPii={shareMaskActive} />
                  </div>
                  {/* Mobile shortcut to AI Reading */}
                  <div className="md:hidden flex justify-center mt-5">
                    <Button
                      data-tour="battu-khaimo-mobile"
                      type="button"
                      className="w-full bg-gold-gradient text-primary-foreground font-semibold hover:opacity-90 rounded-full shadow-gold py-5 text-sm flex items-center justify-center border-0"
                      onClick={() => setActiveTab("reading")}
                    >
                      <Sparkles className="mr-2 h-4 w-4" />
                      Xem Luận Giải Chi Tiết
                    </Button>
                  </div>
                </>
              ) : (
                <div className="flex min-h-[420px] items-center justify-center border border-gold-soft/50 bg-card/25 p-6 text-center text-white/50 rounded-2xl backdrop-blur-sm shadow-mystic">
                  Nhập thông tin và lập lá số để xem Tứ Trụ, Nhật chủ, ngũ hành và đại vận.
                </div>
              )}
            </div>
          </section>

          {chart ? (
            <section
              className={`border border-gold-soft bg-card/65 p-4 sm:p-5 rounded-2xl shadow-mystic backdrop-blur ${
                activeTab === "reading" ? "block" : "hidden md:block"
              }`}
            >
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#E5B869]/10 pb-3">
                <div className="flex items-center gap-2 text-[#E5B869]">
                  <Sparkles className="h-4 w-4" />
                  <h2 className="font-serif-soft text-lg">Khai mở luận giải</h2>
                </div>
                {reading ? (
                  <Button
                    data-tour="battu-chat"
                    type="button"
                    variant="outline"
                    className="border-[#E5B869]/40 bg-transparent text-[#E5B869] hover:bg-[#E5B869]/10 rounded-full"
                    onClick={() => setShowChat((v) => !v)}
                  >
                    <MessageCircle className="mr-2 h-4 w-4" />
                    {showChat ? "Ẩn hỏi đáp" : "Hỏi thầy thêm"}
                  </Button>
                ) : null}
              </div>

              {!user ? (
                <div className="mt-4 flex flex-col items-start gap-3 border border-white/12 bg-black/25 p-4 text-sm text-white/65 rounded-xl">
                  <span className="inline-flex items-center gap-2 text-white/80">
                    <Lock className="h-4 w-4 text-[#E5B869]" />
                    Đăng nhập để khai mở luận giải và hỏi đáp với thầy.
                  </span>
                  <Button
                    asChild
                    className="bg-[#E5B869] text-black hover:bg-[#f0c979] rounded-full"
                  >
                    <Link to="/login" search={{ returnTo: "/bat-tu" }}>
                      Đăng nhập
                    </Link>
                  </Button>
                </div>
              ) : reading ? (
                <div className="mt-4 space-y-6">
                  {reading.reading_stale ? (
                    <div className="flex flex-col gap-3 rounded-xl border border-[#E5B869]/40 bg-[#E5B869]/10 p-4 text-sm sm:flex-row sm:items-center sm:justify-between">
                      <span className="text-foreground/80 dark:text-white/80">
                        Lá số đã được hiệu chỉnh (trụ tháng/năm tính theo đúng giờ giao tiết).
                        Bài luận bên dưới dựa trên lá số cũ — khai mở lại <b>miễn phí</b> để nhận
                        bài luận mới.
                      </span>
                      <Button
                        size="sm"
                        disabled={readingLoading}
                        onClick={() => void openReading(true)}
                        className="shrink-0 rounded-full bg-[#E5B869] text-black hover:bg-[#f0c979]"
                      >
                        {readingLoading ? "Đang khai mở…" : "Khai mở lại miễn phí"}
                      </Button>
                    </div>
                  ) : null}
                  {gods && (gods.dungThan || gods.hyThan || gods.kyThan) ? (
                    <>
                      <div
                        data-tour="battu-gods"
                        className="grid grid-cols-1 sm:grid-cols-3 gap-4 my-4"
                      >
                        {(() => {
                          const dt = gods.dungThan;
                          if (!dt) return null;
                          return (
                            <button
                              type="button"
                              onClick={() =>
                                setActiveSection({
                                  key: "dung_than",
                                  label: "Dụng Thần",
                                  content_md: dt.desc
                                    ? `${dt.desc}`
                                    : "Dụng thần giúp cân bằng bản mệnh, hỗ trợ đắc lực cho vận trình cuộc đời.",
                                })
                              }
                              className="relative overflow-hidden rounded-xl border border-amber-600/30 bg-gradient-to-br from-amber-50/95 to-amber-100/80 p-4 shadow-[0_10px_24px_rgba(146,92,15,0.10)] flex flex-col items-center text-center transition-all duration-300 hover:scale-[1.02] hover:border-amber-600/60 hover:shadow-[0_14px_30px_rgba(146,92,15,0.16)] cursor-pointer w-full group dark:border-amber-500/30 dark:from-amber-950/20 dark:to-black/40 dark:shadow-[0_0_15px_rgba(229,184,105,0.15)] dark:hover:border-amber-500/70 dark:hover:shadow-[0_0_20px_rgba(229,184,105,0.3)]"
                            >
                              <div className="absolute -top-6 -left-6 w-12 h-12 rounded-full bg-amber-500/10 blur-xl pointer-events-none" />
                              <span className="text-[10px] uppercase tracking-widest text-[#E5B869] font-bold opacity-80 flex items-center gap-1">
                                Dụng Thần{" "}
                                <Sparkles className="h-3 w-3 opacity-50 group-hover:opacity-100 transition-opacity" />
                              </span>
                              <div className="flex gap-2 justify-center mt-1">
                                {dt.elements.map((el) => {
                                  const colorClass = ELEMENT_TEXT_COLORS[el] || "text-[#E5B869]";
                                  return (
                                    <span
                                      key={el}
                                      className={`text-2xl font-extrabold font-garamond ${colorClass}`}
                                    >
                                      {el}
                                    </span>
                                  );
                                })}
                              </div>
                              <span className="mt-1.5 text-xs text-amber-800/75 italic leading-snug max-w-[90%] truncate dark:text-amber-200/80">
                                {dt.desc ? truncateDesc(dt.desc) : "Nhấp xem chi tiết luận giải"}
                              </span>
                              <span className="mt-1 text-[9px] text-[#E5B869]/50 group-hover:text-[#E5B869] transition duration-300">
                                (Ấn để xem chi tiết)
                              </span>
                            </button>
                          );
                        })()}
                        {(() => {
                          const ht = gods.hyThan;
                          if (!ht) return null;
                          return (
                            <button
                              type="button"
                              onClick={() =>
                                setActiveSection({
                                  key: "hy_than",
                                  label: "Hỷ Thần",
                                  content_md: ht.desc
                                    ? `${ht.desc}`
                                    : "Hỷ thần sinh trợ cho dụng thần, mang lại cát lành và may mắn.",
                                })
                              }
                              className="relative overflow-hidden rounded-xl border border-zinc-500/30 bg-gradient-to-br from-zinc-50/95 to-zinc-200/70 p-4 shadow-[0_10px_24px_rgba(63,63,70,0.10)] flex flex-col items-center text-center transition-all duration-300 hover:scale-[1.02] hover:border-zinc-500/60 hover:shadow-[0_14px_30px_rgba(63,63,70,0.14)] cursor-pointer w-full group dark:from-zinc-900/20 dark:to-black/40 dark:shadow-[0_0_15px_rgba(255,255,255,0.08)] dark:hover:border-zinc-400 dark:hover:shadow-[0_0_20px_rgba(255,255,255,0.15)]"
                            >
                              <div className="absolute -top-6 -left-6 w-12 h-12 rounded-full bg-zinc-500/10 blur-xl pointer-events-none" />
                              <span className="text-[10px] uppercase tracking-widest text-zinc-700 font-bold opacity-80 flex items-center gap-1 dark:text-zinc-300">
                                Hỷ Thần{" "}
                                <Sparkles className="h-3 w-3 opacity-50 group-hover:opacity-100 transition-opacity" />
                              </span>
                              <div className="flex gap-2 justify-center mt-1">
                                {ht.elements.map((el) => {
                                  const colorClass = ELEMENT_TEXT_COLORS[el] || "text-zinc-100";
                                  return (
                                    <span
                                      key={el}
                                      className={`text-2xl font-extrabold font-garamond ${colorClass}`}
                                    >
                                      {el}
                                    </span>
                                  );
                                })}
                              </div>
                              <span className="mt-1.5 text-xs text-zinc-700/70 italic leading-snug max-w-[90%] truncate dark:text-zinc-300/80">
                                {ht.desc ? truncateDesc(ht.desc) : "Nhấp xem chi tiết luận giải"}
                              </span>
                              <span className="mt-1 text-[9px] text-zinc-600/60 transition duration-300 group-hover:text-zinc-800 dark:text-zinc-400/50 dark:group-hover:text-zinc-200">
                                (Ấn để xem chi tiết)
                              </span>
                            </button>
                          );
                        })()}
                        {(() => {
                          const kt = gods.kyThan;
                          if (!kt) return null;
                          return (
                            <button
                              type="button"
                              onClick={() =>
                                setActiveSection({
                                  key: "ky_than",
                                  label: "Kỵ Thần",
                                  content_md: kt.desc
                                    ? `${kt.desc}`
                                    : "Kỵ thần gây mất cân bằng bản mệnh, nên tránh hoặc hóa giải.",
                                })
                              }
                              className="relative overflow-hidden rounded-xl border border-rose-500/35 bg-gradient-to-br from-rose-50/95 to-rose-100/80 p-4 shadow-[0_10px_24px_rgba(190,18,60,0.09)] flex flex-col items-center text-center transition-all duration-300 hover:scale-[1.02] hover:border-rose-500/65 hover:shadow-[0_14px_30px_rgba(190,18,60,0.14)] cursor-pointer w-full group dark:border-rose-950/40 dark:from-rose-950/10 dark:to-black/45 dark:shadow-[0_0_15px_rgba(244,63,94,0.05)] dark:hover:border-rose-800/80 dark:hover:shadow-[0_0_20px_rgba(244,63,94,0.15)]"
                            >
                              <div className="absolute -top-6 -left-6 w-12 h-12 rounded-full bg-rose-500/5 blur-xl pointer-events-none" />
                              <span className="text-[10px] uppercase tracking-widest text-rose-400/80 font-bold opacity-80 flex items-center gap-1">
                                Kỵ Thần{" "}
                                <Sparkles className="h-3 w-3 opacity-50 group-hover:opacity-100 transition-opacity" />
                              </span>
                              <div className="flex gap-2 justify-center mt-1">
                                {kt.elements.map((el) => {
                                  const colorClass = ELEMENT_TEXT_COLORS[el] || "text-rose-300";
                                  return (
                                    <span
                                      key={el}
                                      className={`text-2xl font-extrabold font-garamond ${colorClass}`}
                                    >
                                      {el}
                                    </span>
                                  );
                                })}
                              </div>
                              <span className="mt-1.5 text-xs text-rose-700/70 italic leading-snug max-w-[90%] truncate dark:text-rose-300/75">
                                {kt.desc ? truncateDesc(kt.desc) : "Nhấp xem chi tiết luận giải"}
                              </span>
                              <span className="mt-1 text-[9px] text-rose-600/60 transition duration-300 group-hover:text-rose-700 dark:text-rose-400/50 dark:group-hover:text-rose-300">
                                (Ấn để xem chi tiết)
                              </span>
                            </button>
                          );
                        })()}
                      </div>

                      {/* Tips explanation block */}
                      <div className="text-xs text-foreground/75 bg-background/55 border border-border/45 rounded-xl p-3.5 flex items-start gap-2.5 max-w-4xl mx-auto shadow-sm backdrop-blur-sm animate-in fade-in duration-300 dark:text-white/75 dark:bg-white/5 dark:border-white/5">
                        <Info className="h-4 w-4 text-[#E5B869] shrink-0 mt-0.5" />
                        <div className="leading-relaxed text-left">
                          <span className="inline-block">
                            <strong className="text-[#E5B869] font-semibold">Dụng Thần:</strong>{" "}
                            Hành giúp cân bằng và điều hòa chân khí ngũ hành trong Tứ Trụ.
                          </span>
                          <span className="mx-2 text-muted-foreground/40 hidden md:inline dark:text-white/20">
                            |
                          </span>
                          <span className="inline-block">
                            <strong className="text-zinc-700 font-semibold dark:text-zinc-200">
                              Hỷ Thần:
                            </strong>{" "}
                            Hành sinh trợ, nâng đỡ cho Dụng Thần (mang cát lành).
                          </span>
                          <span className="mx-2 text-muted-foreground/40 hidden md:inline dark:text-white/20">
                            |
                          </span>
                          <span className="inline-block">
                            <strong className="text-rose-400/90 font-semibold">Kỵ Thần:</strong>{" "}
                            Hành khắc chế hoặc gây mất cân bằng bản mệnh (nên hạn chế).
                          </span>
                        </div>
                      </div>
                    </>
                  ) : null}

                  <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.15fr_1fr] gap-6 items-start mt-6 pt-4 border-t border-border/35 dark:border-white/5">
                    {/* Left Column (detailed sections - Talisman cards) */}
                    <div className="flex flex-col gap-6 order-2 lg:order-1 lg:col-start-1 lg:row-start-1">
                      {leftSections.map((sec) => (
                        <button
                          key={sec.key}
                          type="button"
                          data-tour="battu-sections"
                          onClick={() => setActiveSection(sec)}
                          className="talisman w-full text-center py-10 px-5 transition-all duration-300 hover:scale-[1.02] hover:-translate-y-1 cursor-pointer flex flex-col items-center justify-center group relative overflow-hidden min-h-[160px] border border-[#E5B869]/20 hover:border-[#E5B869]/80 hover:shadow-[0_0_30px_rgba(229,184,105,0.25)]"
                        >
                          <div className="absolute inset-0 bg-radial-gradient from-[#E5B869]/0 to-[#E5B869]/0 group-hover:from-[#E5B869]/5 group-hover:to-transparent transition-all duration-500 pointer-events-none" />
                          <div className="absolute top-3 right-3 opacity-30 group-hover:opacity-100 transition-all duration-500 text-[#E5B869]">
                            <Sparkles className="h-4 w-4 animate-pulse group-hover:scale-110 transition-transform" />
                          </div>
                          <span className="text-lg md:text-xl font-garamond font-bold uppercase tracking-[0.2em] text-[#b87912] transition duration-300 drop-shadow-[0_1px_2px_rgba(255,255,255,0.85)] group-hover:text-[#9f650c] dark:text-[#E5B869] dark:drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)] dark:group-hover:text-amber-300">
                            {sec.label || sec.key}
                          </span>
                          <div className="w-12 h-[1px] bg-gradient-to-r from-transparent via-[#E5B869]/40 to-transparent my-3 group-hover:via-[#E5B869]/80 transition duration-300" />
                          <span className="text-xs font-sans font-light italic text-foreground/55 transition duration-300 max-w-[85%] text-center leading-normal group-hover:text-foreground/75 dark:text-white/40 dark:group-hover:text-white/70">
                            {getPoeticSubtext(sec.key, sec.label)}
                          </span>
                        </button>
                      ))}
                    </div>

                    {/* Center Column (Core Reading - Đại Cục) */}
                    <div
                      data-tour="battu-daicuc"
                      className="flex flex-col items-center justify-center p-6 md:p-8 relative overflow-hidden order-1 lg:order-2 lg:col-start-2 lg:row-start-1 bg-transparent"
                    >
                      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full bg-[#E5B869]/3 blur-3xl pointer-events-none" />
                      <h2
                        data-tour="battu-daicuc-anchor"
                        className="bazi-gold-heading font-garamond font-bold uppercase text-2xl md:text-3xl tracking-[0.2em] text-center"
                      >
                        Đại Cục Bản Mệnh
                      </h2>
                      <MagicalDivider />
                      <article className="prose prose-sm max-w-none px-2 text-justify font-sans text-[15.5px] font-normal leading-[1.9] text-foreground/82 prose-strong:text-[#b87912] md:px-4 dark:prose-invert dark:font-light dark:text-[#EAE0D5] dark:prose-strong:text-[#E5B869]">
                        <ReactMarkdown
                          remarkPlugins={MARKDOWN_REMARK_PLUGINS}
                          components={READING_MD_COMPONENTS}
                        >
                          {reading.dai_cuc}
                        </ReactMarkdown>
                        <AiWatermark className="not-prose justify-center" />
                      </article>
                      <MagicalDivider />
                    </div>

                    {/* Right Column (detailed sections - Talisman cards) */}
                    <div className="flex flex-col gap-6 order-3 lg:order-3 lg:col-start-3 lg:row-start-1">
                      {rightSections.map((sec) => (
                        <button
                          key={sec.key}
                          type="button"
                          data-tour="battu-sections"
                          onClick={() => setActiveSection(sec)}
                          className="talisman w-full text-center py-10 px-5 transition-all duration-300 hover:scale-[1.02] hover:-translate-y-1 cursor-pointer flex flex-col items-center justify-center group relative overflow-hidden min-h-[160px] border border-[#E5B869]/20 hover:border-[#E5B869]/80 hover:shadow-[0_0_30px_rgba(229,184,105,0.25)]"
                        >
                          <div className="absolute inset-0 bg-radial-gradient from-[#E5B869]/0 to-[#E5B869]/0 group-hover:from-[#E5B869]/5 group-hover:to-transparent transition-all duration-500 pointer-events-none" />
                          <div className="absolute top-3 right-3 opacity-30 group-hover:opacity-100 transition-all duration-500 text-[#E5B869]">
                            <Sparkles className="h-4 w-4 animate-pulse group-hover:scale-110 transition-transform" />
                          </div>
                          <span className="text-lg md:text-xl font-garamond font-bold uppercase tracking-[0.2em] text-[#b87912] transition duration-300 drop-shadow-[0_1px_2px_rgba(255,255,255,0.85)] group-hover:text-[#9f650c] dark:text-[#E5B869] dark:drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)] dark:group-hover:text-amber-300">
                            {sec.label || sec.key}
                          </span>
                          <div className="w-12 h-[1px] bg-gradient-to-r from-transparent via-[#E5B869]/40 to-transparent my-3 group-hover:via-[#E5B869]/80 transition duration-300" />
                          <span className="text-xs font-sans font-light italic text-foreground/55 transition duration-300 max-w-[85%] text-center leading-normal group-hover:text-foreground/75 dark:text-white/40 dark:group-hover:text-white/70">
                            {getPoeticSubtext(sec.key, sec.label)}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="mt-4 flex flex-col items-start gap-3 text-sm text-foreground/80">
                  <p className="leading-relaxed text-muted-foreground">
                    Khai mở để thầy luận giải toàn bộ lá số: tính cách, sự nghiệp, tài lộc, tình
                    duyên, sức khỏe, đại vận và dụng thần cải vận.{" "}
                    {isFirstReadingFree ? (
                      <span className="text-[#E5B869]">Lá số đầu tiên miễn phí.</span>
                    ) : (
                      <span className="text-[#E5B869]">
                        Phí khai mở: {readingPrice.toLocaleString("vi-VN")}đ.
                      </span>
                    )}
                  </p>
                  <Button
                    data-tour="battu-khaimo"
                    type="button"
                    disabled={readingLoading}
                    onClick={() => void openReading()}
                    className="bg-[#E5B869] text-black hover:bg-[#f0c979] rounded-full"
                  >
                    {readingLoading ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Sparkles className="mr-2 h-4 w-4" />
                    )}
                    Khai mở luận giải
                    <span className="ml-2 rounded-full bg-black/15 px-2 py-0.5 text-xs font-semibold">
                      {readingPriceLabel}
                    </span>
                  </Button>
                </div>
              )}
            </section>
          ) : null}

          {/* Scroll Overlay for Focus Mode */}
          {activeSection && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-xl p-4 md:p-6 transition-all duration-500 animate-in fade-in"
              onClick={() => setActiveSection(null)}
            >
              <div
                className="relative w-full max-w-2xl max-h-[85vh] flex flex-col items-center animate-mystic-rise animate-in fade-in zoom-in-95 duration-300"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Roller Top */}
                <div className="h-4 w-[102%] bg-gradient-to-r from-[#5c3a21] via-[#aa7a54] to-[#5c3a21] rounded-full shadow-[0_4px_10px_rgba(0,0,0,0.5)] border border-[#E5B869]/30 z-10 flex justify-between items-center px-4">
                  <div className="w-2 h-2 bg-[#E5B869]/60 rounded-full" />
                  <div className="w-2 h-2 bg-[#E5B869]/60 rounded-full" />
                </div>

                {/* Silk Scroll Body */}
                <div className="w-full flex-1 overflow-hidden bg-gradient-to-b from-amber-50/98 to-stone-50/99 border-x border-[#b8860b]/35 shadow-[0_24px_60px_rgba(92,64,36,0.18)] flex flex-col p-6 md:p-10 my-0.5 rounded-sm relative dark:from-[#1e1e1b]/98 dark:to-[#121211]/99 dark:border-[#E5B869]/30 dark:shadow-[0_0_50px_rgba(229,184,105,0.25)]">
                  {/* Decorative internal thin border */}
                  <div className="absolute inset-2 border border-[#E5B869]/10 pointer-events-none rounded-sm" />

                  {/* Title */}
                  <h3 className="bazi-gold-heading font-garamond font-bold uppercase text-xl md:text-2xl tracking-[0.2em] text-center border-b border-[#b8860b]/25 pb-3 mb-4 dark:border-[#E5B869]/20">
                    {activeSection.label || activeSection.key}
                  </h3>

                  {/* Scrollable Content */}
                  <div className="overflow-y-auto pr-2 flex-1 mt-4 scrollbar-thin scrollbar-thumb-gold/20 my-4 ink-bleed">
                    <article className="prose prose-sm max-w-none px-2 text-justify font-sans text-[15px] font-normal leading-[1.9] text-foreground/85 prose-strong:text-[#b87912] md:px-4 md:text-[16px] dark:prose-invert dark:text-[#F4E8D7] dark:prose-strong:text-[#FFD579]">
                      <ReactMarkdown
                        remarkPlugins={MARKDOWN_REMARK_PLUGINS}
                        components={READING_MD_COMPONENTS}
                      >
                        {activeSection.content_md}
                      </ReactMarkdown>
                      <AiWatermark className="not-prose" />
                    </article>
                  </div>

                  {/* Close button: "Thu lại" / Up Arrow */}
                  <button
                    type="button"
                    onClick={() => setActiveSection(null)}
                    className="mt-6 flex flex-col items-center gap-1 group cursor-pointer text-[#E5B869] hover:text-amber-300 transition duration-300 self-center"
                  >
                    <ChevronUp className="h-5 w-5 text-[#E5B869] group-hover:-translate-y-1 transition-transform duration-300 animate-bounce" />
                    <span className="text-xs uppercase tracking-[0.2em] border-b border-[#E5B869]/40 group-hover:border-amber-300 pb-0.5 font-serif-soft">
                      Thu lại
                    </span>
                  </button>
                </div>

                {/* Roller Bottom */}
                <div className="h-4 w-[102%] bg-gradient-to-r from-[#5c3a21] via-[#aa7a54] to-[#5c3a21] rounded-full shadow-[0_-4px_10px_rgba(0,0,0,0.5)] border border-[#E5B869]/30 z-10 flex justify-between items-center px-4">
                  <div className="w-2 h-2 bg-[#E5B869]/60 rounded-full" />
                  <div className="w-2 h-2 bg-[#E5B869]/60 rounded-full" />
                </div>
              </div>
            </div>
          )}

          {/* Backdrop Overlay for Chat Drawer */}
          {showChat && (
            <div
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 transition-opacity duration-500 animate-in fade-in"
              onClick={() => setShowChat(false)}
            />
          )}

          {/* Sliding Chat Drawer */}
          <div
            className={`app-drawer-viewport fixed top-0 right-0 z-50 flex flex-col bg-[#fdfcf9]/95 dark:bg-[#121211]/95 border-l border-[#b8860b]/20 dark:border-[#E5B869]/20 shadow-[0_0_30px_rgba(0,0,0,0.08)] dark:shadow-[0_0_30px_rgba(0,0,0,0.8)] transition-all duration-500 ease-in-out backdrop-blur-md ${
              showChat
                ? "translate-x-0 w-full md:w-[480px]"
                : "translate-x-full w-full md:w-[480px]"
            }`}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#b8860b]/15 dark:border-[#E5B869]/10 bg-[#f6ebd4]/90 dark:bg-[#1e1e1b]/90 shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                <span className="font-serif-soft text-sm font-semibold text-[#E5B869] uppercase tracking-wider">
                  Đàm Đạo Bản Mệnh
                </span>
              </div>
              <button
                type="button"
                onClick={() => setShowChat(false)}
                className="rounded-full p-1.5 text-foreground/50 dark:text-white/50 hover:text-[#E5B869] hover:bg-black/5 dark:hover:bg-white/5 transition-all duration-300 active:scale-95"
                aria-label="Đóng chat"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Chat Body */}
            <div className="flex-1 min-h-0 p-1 pb-[max(0.25rem,env(safe-area-inset-bottom))] sm:p-4 sm:pb-[max(1rem,env(safe-area-inset-bottom))]">
              {chart && showChat && (
                <MysticChat
                  compact
                  kind="copilot"
                  variant="scroll"
                  fortuneType="bazi"
                  baziChartId={chart.id}
                  subject={`lá số Bát Tự của ${chart.ho_ten}`}
                  placeholder="Hỏi sâu hơn về sự nghiệp, tài lộc, tình duyên..."
                  initialSessionId={historySessionId}
                  initialMessages={historyMessages}
                  resetKey={historySessionId ?? chart.id}
                />
              )}
            </div>
          </div>
        </div>
      </main>
      <TrustProbeDialog
        probeId={trustProbeId}
        open={trustProbeOpen}
        onOpenChange={setTrustProbeOpen}
      />
      {chart ? (
        <ShareDialog
          open={shareOpen}
          onOpenChange={setShareOpen}
          resourceType="bazi"
          resourceId={chart.id}
          captureRef={chartCaptureRef}
          onMaskChange={setShareMaskActive}
        />
      ) : null}
      <Sheet open={chartsDrawerOpen} onOpenChange={setChartsDrawerOpen}>
        <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-md bg-background">
          <SheetHeader>
            <SheetTitle>Lá số Bát Tự đã lập</SheetTitle>
          </SheetHeader>
          <div className="mt-4 flex flex-col gap-2">
            {savedCharts.isLoading && (
              <div className="flex justify-center py-10">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            )}
            {!savedCharts.isLoading && savedChartItems.length === 0 && (
              <p className="py-10 text-center text-sm text-muted-foreground">
                Chưa có lá số nào. Hãy lập lá số đầu tiên.
              </p>
            )}
            {savedChartItems.map((c) => {
              const isActive = c.id === chart?.id;
              const isDeleting = deletingChartId === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => {
                    void loadChart(c.id);
                    setChartsDrawerOpen(false);
                  }}
                  className={`group flex items-center gap-3 rounded-xl border p-3 text-left transition hover:border-primary/60 ${
                    isActive
                      ? "border-primary/60 bg-primary/10"
                      : "border-border/60 bg-background/40"
                  }`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-serif-soft text-base text-gold-gradient">
                      {c.ho_ten || "Ẩn danh"}
                    </div>
                    <div className="truncate text-[11px] text-muted-foreground">
                      {[
                        c.gioi_tinh === "nu" || c.gioi_tinh === "nữ" ? "Nữ" : "Nam",
                        c.nam_sinh ? `Năm ${c.nam_sinh}` : null,
                        c.day_master ? `Nhật chủ ${c.day_master}` : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </div>
                  </div>
                  <span
                    role="button"
                    tabIndex={-1}
                    onClick={(e) => {
                      e.stopPropagation();
                      void deleteChart(c.id);
                    }}
                    className="shrink-0 text-destructive/70 transition hover:text-destructive p-1"
                    aria-label="Xoá lá số"
                  >
                    {isDeleting ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </span>
                </button>
              );
            })}

            {savedChartsPages > 1 && (
              <nav
                aria-label="Phân trang lá số Bát Tự"
                className="mt-2 flex items-center justify-between gap-2 text-xs text-muted-foreground"
              >
                <button
                  type="button"
                  disabled={chartsPage <= 1 || savedCharts.isFetching}
                  onClick={() => setChartsPage((p) => Math.max(1, p - 1))}
                  className="rounded-md border border-border/60 bg-background/40 px-3 py-1 transition hover:text-foreground disabled:opacity-40"
                >
                  Trước
                </button>
                <span aria-live="polite">
                  Trang {Math.min(chartsPage, savedChartsPages)}/{savedChartsPages} ·{" "}
                  {savedChartsTotal} lá số
                </span>
                <button
                  type="button"
                  disabled={chartsPage >= savedChartsPages || savedCharts.isFetching}
                  onClick={() => setChartsPage((p) => Math.min(savedChartsPages, p + 1))}
                  className="rounded-md border border-border/60 bg-background/40 px-3 py-1 transition hover:text-foreground disabled:opacity-40"
                >
                  Sau
                </button>
              </nav>
            )}
          </div>
        </SheetContent>
      </Sheet>
      {confirmDialog}
    </div>
  );
}
